import React, { useState } from 'react';
import dayjs from 'dayjs';
import { LEAVE_COLOR } from '../constants';
import ImageZoomDialog from './ImageZoomDialog';

const OV = { position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const MO = { background: '#fff', borderRadius: 8, maxWidth: 540, width: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto' };
const DIV = '1px solid #e5e7eb';

const InfoBox = ({ style, children }) => (
  <div style={{ borderRadius: 6, padding: '10px 14px', ...style }}>{children}</div>
);

const KVRow = ({ label, value, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{label}</span>
    {children || <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{value}</span>}
  </div>
);

// ชั่วโมงทำงานจริงจากเวลาเข้า-ออก (นาที): เข้าก่อน 08:00 นับจาก 08:00, หักพักเที่ยง 60 นาที (ยกเว้นเข้าหลังเที่ยง/ออกก่อน 13:00), หักสาย, หักไม่เช็คเอาท์/ข้ามวัน
const computeHours = (checkin, attendanceRules) => {
  if (!checkin?.checkIn) return null;
  const ci = dayjs(checkin.checkIn);
  const ciH = ci.hour(), ciM = ci.minute();
  const ciMin = ciH * 60 + ciM;
  const workStartMin = 8 * 60, workEndMin = 17 * 60;
  const coMin = checkin.checkOut ? dayjs(checkin.checkOut).hour() * 60 + dayjs(checkin.checkOut).minute() : null;
  // checkOut วันเดียวกับ checkIn แต่เวลา (นาฬิกา) น้อยกว่า checkIn ก็ถือว่าข้ามวันเหมือนกัน
  // (เช่น ลืมเช็คเอาท์แล้วมาปิดงานเช้าวันถัดไป แต่ถูกเซฟวันที่ผิดเป็นวันเดียวกับ checkIn)
  const isNextDayOrNoCheckout = !checkin.checkOut ||
    dayjs(checkin.checkOut).format('YYYY-MM-DD') !== ci.format('YYYY-MM-DD') ||
    coMin < ciMin;
  const isLunchPeriodCheckin = ciH === 12;
  const effectiveStartMin = isLunchPeriodCheckin ? 13 * 60 : Math.max(ciMin, workStartMin);
  const rawEffectiveEndMin = isNextDayOrNoCheckout ? workEndMin : Math.min(coMin, workEndMin);
  const isAfterLunch = ciH >= 12;
  const isEarlyCheckout = !isAfterLunch && !isNextDayOrNoCheckout && coMin != null && coMin < 13 * 60;
  const effectiveEndMin = isEarlyCheckout ? Math.min(rawEffectiveEndMin, 12 * 60) : rawEffectiveEndMin;
  const grossMin = Math.max(0, effectiveEndMin - effectiveStartMin);
  const afterLunchMin = isAfterLunch || isEarlyCheckout ? grossMin : Math.max(0, grossMin - 60);
  const lateMin = Math.max(0, ciMin - workStartMin);
  const lateRule = (attendanceRules || []).find((r) => r.type === 'LATE_ARRIVAL' && r.enabled);
  const isLateDeducted = !isAfterLunch && lateRule && lateMin >= lateRule.threshold_minutes;
  const lateDeductMin = isLateDeducted ? lateRule.deduct_minutes : 0;
  const nextDayRule = (attendanceRules || []).find((r) => r.type === 'LATE_CHECKOUT_NEXT_DAY' && r.enabled);
  const noCoDeductMin = isNextDayOrNoCheckout && nextDayRule ? nextDayRule.deduct_minutes : 0;
  const finalMin = Math.max(0, afterLunchMin - lateDeductMin - noCoDeductMin);
  const finalHr = Math.round((finalMin / 60) * 100) / 100;
  return {
    workStartMin, isNextDayOrNoCheckout, isLunchPeriodCheckin, effectiveStartMin, effectiveEndMin,
    isEarlyCheckout, grossMin, isAfterLunch, isLateDeducted, lateDeductMin, noCoDeductMin,
    finalMin, finalHr,
  };
};

const LEAVE_NAME = {
  SICK: 'ลาป่วย', PERSONAL: 'ลากิจ', VACATION: 'ลาพักร้อน',
  MATERNITY: 'ลาคลอด', ORDINATION: 'ลาบวช', UNPAID: 'ลาไม่รับค่าจ้าง',
};
const PERIOD_NAME = { FULL_DAY: 'เต็มวัน', MORNING: 'ครึ่งวันเช้า', AFTERNOON: 'ครึ่งวันบ่าย' };
const DOW_FULL = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
const fmtBaht = (n) => `฿${Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Switch = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    style={{
      position: 'relative', width: 40, height: 22, flexShrink: 0, padding: 0, border: 'none', borderRadius: 11,
      background: checked ? '#16a34a' : '#cbd5e1', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
      transition: 'background .15s',
    }}
  >
    <span style={{
      position: 'absolute', top: 2, left: checked ? 20 : 2, width: 18, height: 18, borderRadius: '50%',
      background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.25)', transition: 'left .15s',
    }} />
  </button>
);

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 16, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
    <div style={{ width: 88, flexShrink: 0, fontSize: '0.8rem', color: '#64748b' }}>{label}</div>
    <div style={{ flex: 1, minWidth: 0, fontSize: '0.875rem', color: '#0f172a', wordBreak: 'break-word' }}>{children}</div>
  </div>
);

// รายละเอียดการลา + ติ๊กให้ค่าแรงวันลา (ใช้ทั้งกรณีวันลาล้วน และวันที่ลาครึ่งวันแล้วมีลงเวลาอีกครึ่ง)
const LeaveDetail = ({ dvData, canEdit, onWageChange }) => {
  const lv = dvData.leave;
  const paid = !!dvData.wagePaid;
  const spd = Number(dvData.spd || 0);
  const fmtD = (d) => dayjs(d).format('DD/MM/YYYY');
  const multi = lv.start_date && lv.end_date && !dayjs(lv.start_date).isSame(dayjs(lv.end_date), 'day');
  const spanDays = multi ? dayjs(lv.end_date).startOf('day').diff(dayjs(lv.start_date).startOf('day'), 'day') + 1 : 1;
  const credit = lv.period === 'FULL_DAY' ? 1 : 0.5;
  const amt = credit * spd;
  return (
    <>
      <Field label="ประเภท">
        {LEAVE_NAME[lv.leave_type] || lv.leave_type || 'ลา'} · {PERIOD_NAME[lv.period] || PERIOD_NAME.FULL_DAY}
      </Field>
      <Field label="ช่วงที่ลา">
        {multi ? `${fmtD(lv.start_date)} – ${fmtD(lv.end_date)} (${spanDays} วัน)` : fmtD(lv.start_date || lv.end_date)}
      </Field>
      <Field label="เหตุผล">
        {lv.reason ? lv.reason : <span style={{ color: '#94a3b8' }}>ไม่ได้ระบุ</span>}
      </Field>

      <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 8, background: paid ? '#f0fdf4' : '#f8fafc', border: `1px solid ${paid ? '#bbf7d0' : '#e2e8f0'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>ให้ค่าแรงวันลานี้</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
              {dvData.dayNum ? `มีผลเฉพาะวันที่ ${String(dvData.dayNum).padStart(2, '0')}` : 'มีผลเฉพาะวันนี้'}
              {!canEdit && ' · ไม่มีสิทธิ์แก้ไข'}
            </div>
          </div>
          {onWageChange && dvData.dayNum && (
            <Switch checked={paid} disabled={!canEdit} onChange={onWageChange} label="ให้ค่าแรงวันลานี้" />
          )}
        </div>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${paid ? '#bbf7d0' : '#e2e8f0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
            {paid ? `${credit === 1 ? '1 วัน' : 'ครึ่งวัน'} × ${fmtBaht(spd)}` : 'ไม่นับเป็นค่าแรง'}
          </span>
          <span style={{ fontSize: '1.05rem', fontWeight: 700, color: paid ? '#15803d' : '#94a3b8' }}>
            {paid ? fmtBaht(amt) : '—'}
          </span>
        </div>
      </div>
    </>
  );
};

const DayDetailView = React.memo(function DayDetailView({
  open, onClose, dvData, attendanceRules, month, year,
  hrMgmt, onSkipLateDeduct, skipLateDvSaving, saveSkipLateDv,
  canEditLeaveWage, onLeaveWageChange,
}) {
  const [zoomSrc, setZoomSrc] = useState(null);
  const [showReport, setShowReport] = useState(false);
  if (!open) return null;
  const closeAll = () => { setShowReport(false); onClose(); };
  const hoursCalc = dvData?.checkin ? computeHours(dvData.checkin, attendanceRules) : null;
  return (
    <div style={OV} onClick={closeAll}>
      <div style={MO} onClick={(e) => e.stopPropagation()}>
        {/* Title */}
        {!dvData?.checkin && dvData?.leave ? (() => {
          const lc = LEAVE_COLOR[dvData.leave.leave_type] || LEAVE_COLOR.PERSONAL;
          const dt = dvData.dayNum ? dayjs(`${year}-${month + 1}-${dvData.dayNum}`) : null;
          return (
            <div style={{ padding: '18px 20px 14px', borderBottom: DIV, borderTop: `4px solid ${lc.border}`, borderRadius: '8px 8px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ padding: '2px 10px', borderRadius: 12, background: lc.bg, color: lc.text, fontSize: '0.75rem', fontWeight: 700 }}>
                  {LEAVE_NAME[dvData.leave.leave_type] || 'ลา'}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{PERIOD_NAME[dvData.leave.period] || ''}</span>
              </div>
              <div style={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a', marginTop: 8 }}>{dvData?.empName}</div>
              {dt && (
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 2 }}>
                  วัน{DOW_FULL[dt.day()]}ที่ {dt.format('DD/MM/YYYY')}
                </div>
              )}
            </div>
          );
        })() : (
          <div style={{ padding: '14px 20px', borderBottom: DIV }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
              {dvData?.checkin ? 'รายละเอียดการลงเวลา' : 'รายละเอียดการลา'}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: 2 }}>{dvData?.empName}</div>
          </div>
        )}

        <div style={{ padding: '16px 20px' }}>
          {/* Checkin detail */}
          {dvData?.checkin && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>โปรเจค</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginTop: 2 }}>{dvData.checkin.project_number || '—'}</div>
                  <div style={{ fontSize: '0.875rem', color: '#374151' }}>{dvData.checkin.project_name || '—'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>วันที่</div>
                  <div style={{ fontWeight: 700, marginTop: 2 }}>
                    {dvData.checkin.checkIn ? dayjs(dvData.checkin.checkIn).format('DD/MM/YYYY') : '—'}
                  </div>
                  {(dvData.checkin.dailySummary || dvData.checkin.dailySummaryAttachments?.length > 0) && (
                    <button
                      type="button"
                      onClick={() => setShowReport((v) => !v)}
                      style={{ marginTop: 6, padding: '3px 10px', border: '1px solid #2563eb', borderRadius: 14, background: showReport ? '#2563eb' : '#fff', color: showReport ? '#fff' : '#2563eb', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {showReport ? 'ซ่อนรายงาน' : 'ดูรายงาน'}
                    </button>
                  )}
                </div>
              </div>
              {showReport && (dvData.checkin.dailySummary || dvData.checkin.dailySummaryAttachments?.length > 0) && (
                <InfoBox style={{ background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 16 }}>
                  <div style={{ fontSize: '0.72rem', color: '#1d4ed8', fontWeight: 600, marginBottom: 4 }}>สรุปงานประจำวัน</div>
                  <div style={{ fontSize: '0.85rem', color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{dvData.checkin.dailySummary}</div>
                  {dvData.checkin.dailySummaryAttachments?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      {dvData.checkin.dailySummaryAttachments.map((f) => (/\.(png|jpe?g|gif|webp)(\?|$)/i.test(f.url) ? (
                        <img
                          key={f.url}
                          src={f.url}
                          alt={f.name}
                          onClick={() => setZoomSrc(f.url)}
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #bfdbfe', cursor: 'zoom-in' }}
                        />
                      ) : (
                        <a key={f.url} href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', color: '#2563eb' }}>
                          📎 {f.name || 'ไฟล์แนบ'}
                        </a>
                      )))}
                    </div>
                  )}
                </InfoBox>
              )}
              <hr style={{ border: 'none', borderTop: DIV, margin: '0 0 16px' }} />

              {/* Times */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>เวลาเข้า</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{dvData?.checkin?.checkInTime || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>เวลาออก</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{dvData?.checkin?.checkOutTime || '(ยังไม่เช็คเอาท์)'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>ชั่วโมงทำงาน</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#16a34a', lineHeight: 1.3 }}>
                    {hoursCalc ? `${hoursCalc.finalHr} ชม.` : '—'}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>Rate</div>
                  <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 10px', background: '#7c3aed', color: '#fff', borderRadius: 14, fontWeight: 700, fontSize: '0.85rem' }}>
                    {dvData?.checkin?.effectiveRate || dvData?.checkin?.rate || 1}x
                  </span>
                </div>
              </div>
              <hr style={{ border: 'none', borderTop: DIV, margin: '0 0 16px' }} />

              {/* Photos + Calculation */}
              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                  {[
                    { label: 'Check In', photo: dvData?.checkin?.checkInPhoto || dvData?.checkin?.imageIn },
                    { label: 'Check Out', photo: dvData?.checkin?.checkOutPhoto || dvData?.checkin?.imageOut },
                  ].map(({ label, photo }) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      {photo ? (
                        <img
                          src={photo}
                          onClick={() => setZoomSrc(photo)}
                          title="คลิกเพื่อดูรูปขยาย"
                          style={{ width: 80, height: 80, borderRadius: 6, objectFit: 'cover', border: '1px solid #e5e7eb', display: 'block', cursor: 'zoom-in' }}
                          alt={label}
                        />
                      ) : (
                        <div style={{ width: 80, height: 80, background: '#f8fafc', borderRadius: 6, border: '1px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <span style={{ fontSize: 18, color: '#94a3b8' }}>📷</span>
                          <span style={{ fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>ไม่มีรูป</span>
                        </div>
                      )}
                      <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'block', marginTop: 6 }}>{label}</span>
                    </div>
                  ))}
                </div>

                {/* Calculation table */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: 10 }}>วิธีคำนวณ</div>
                  {(() => {
                    const checkin = dvData?.checkin;
                    if (!checkin?.checkIn || !hoursCalc) return null;
                    const ci = dayjs(checkin.checkIn);
                    const ciMin = ci.hour() * 60 + ci.minute();
                    const {
                      workStartMin, isNextDayOrNoCheckout, isLunchPeriodCheckin, effectiveStartMin,
                      effectiveEndMin, isEarlyCheckout, grossMin, isAfterLunch, isLateDeducted,
                      lateDeductMin, noCoDeductMin, finalMin, finalHr,
                    } = hoursCalc;
                    const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
                    const startNote = isLunchPeriodCheckin ? `(เช็คอิน ${fmtMin(ciMin)} → เริ่มนับ 13:00)` : effectiveStartMin > workStartMin ? '(หลัง 08:00)' : '';
                    const rows = [
                      { label: 'เริ่มนับ', value: fmtMin(effectiveStartMin), note: startNote },
                      { label: 'สิ้นสุด', value: fmtMin(effectiveEndMin), note: isNextDayOrNoCheckout ? '(cap 17:00)' : isEarlyCheckout ? '(cap 12:00)' : '' },
                      { label: 'รวม', value: `${(grossMin / 60).toFixed(2)} ชม. (${grossMin} นาที)` },
                      isAfterLunch ? { label: 'พักเที่ยง', value: 'ไม่หัก (เข้างานหลัง 12:00)', color: '#16a34a' }
                        : isEarlyCheckout ? { label: 'พักเที่ยง', value: 'ไม่หัก (ออกก่อน 13:00)', color: '#16a34a' }
                        : { label: 'หักพักเที่ยง', value: '−1.00 ชม. (−60 นาที)', color: '#6b7280' },
                      ...(isLateDeducted ? [{ label: 'หักสาย', value: `−${(lateDeductMin / 60).toFixed(2)} ชม. (−${lateDeductMin} นาที)`, color: '#f97316' }] : []),
                      ...(noCoDeductMin > 0 ? [{ label: !checkin.checkOut ? 'ไม่เช็คเอาท์' : 'เช็คออกข้ามวัน', value: `−${(noCoDeductMin / 60).toFixed(2)} ชม. (−${noCoDeductMin} นาที)`, color: '#ef4444' }] : []),
                      { label: 'ชม.ที่ใช้', value: `${finalHr} ชม. (${finalMin} นาที)`, bold: !dvData?.leaveAutoH },
                      ...(dvData?.leaveAutoH > 0 ? [
                        { label: '+ลาครึ่งวัน', value: `+${dvData.leaveAutoH} ชม.`, color: '#15803d' },
                        { label: 'รวมทั้งหมด', value: `${Math.round((finalHr + dvData.leaveAutoH) * 100) / 100} ชม.`, bold: true, color: '#15803d' },
                      ] : []),
                    ];
                    return (
                      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i} style={row.bold ? { borderTop: DIV } : {}}>
                              <td style={{ padding: '3px 6px 3px 0', fontSize: '0.72rem', color: row.color || (row.bold ? 'inherit' : '#6b7280'), fontWeight: row.bold ? 700 : 400, whiteSpace: 'nowrap' }}>{row.label}</td>
                              <td style={{ padding: '3px 0', fontSize: '0.72rem', fontWeight: row.bold ? 700 : 400, color: row.color || 'inherit', textAlign: 'right', whiteSpace: 'nowrap' }}>{row.value}</td>
                              {row.note && <td style={{ padding: '3px 0 3px 4px', fontSize: '0.65rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{row.note}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </>
          )}

          {/* Leave info */}
          {dvData?.leave && (
            <>
              {dvData?.checkin && (
                <>
                  <hr style={{ border: 'none', borderTop: DIV, margin: '16px 0 4px' }} />
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', padding: '8px 0 0' }}>การลาในวันนี้</div>
                </>
              )}
              <LeaveDetail dvData={dvData} canEdit={canEditLeaveWage} onWageChange={onLeaveWageChange} />
            </>
          )}

          {/* HR override / manual */}
          {(dvData?.manualEntry || dvData?.checkin?.salary_override?.amount != null) && (
            <>
              <hr style={{ border: 'none', borderTop: DIV, margin: '16px 0 12px' }} />
              {dvData?.manualEntry && (() => {
                const mn = dvData.manualEntry;
                const effAmt = mn.amount != null ? Number(mn.amount) : (Number(mn.hours || 0) / 8) * Number(mn.salary_per_day || 0);
                return (
                  <InfoBox style={{ background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 8 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1d4ed8', marginBottom: 8 }}>✎ เพิ่มชั่วโมงโดย HR</div>
                    <KVRow label="โดย"><span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b' }}>{mn.edited_by || '—'}</span></KVRow>
                    <KVRow label="เพิ่มชั่วโมง"><span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1d4ed8' }}>{mn.hours} ชม.</span></KVRow>
                    <KVRow label="ค่าแรง">
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1d4ed8' }}>
                        ฿{effAmt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        <span style={{ color: '#93c5fd', fontWeight: 400, marginLeft: 4, fontSize: '0.68rem' }}>{mn.amount != null ? '(กำหนดเอง)' : '(อัตโนมัติ)'}</span>
                      </span>
                    </KVRow>
                  </InfoBox>
                );
              })()}
              {(() => {
                if (dvData?.checkin?.salary_override?.amount == null) return false;
                if (!dvData?.manualEntry) return true;
                const mn = dvData.manualEntry;
                const effAmt = mn.amount != null ? mn.amount : (mn.hours / 8) * (mn.salary_per_day || 0);
                return Math.abs(dvData.checkin.salary_override.amount - effAmt) > 0.01;
              })() && (
                <InfoBox style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b45309', marginBottom: 8 }}>✎ แก้ไขค่าแรงโดย HR</div>
                  <KVRow label="โดย"><span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{dvData.checkin.salary_override.edited_by || '—'}</span></KVRow>
                  {dvData.checkin.salary_override.edited_at && (
                    <KVRow label="เมื่อ"><span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{dayjs(dvData.checkin.salary_override.edited_at).format('DD/MM/YY HH:mm')}</span></KVRow>
                  )}
                  <KVRow label="ค่าแรง">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.72rem', color: '#9ca3af', textDecoration: 'line-through' }}>
                        ฿{dvData.autoAmt != null ? Number(dvData.autoAmt).toLocaleString('th-TH', { minimumFractionDigits: 2 }) : '—'}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>→</span>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b45309' }}>
                        ฿{Number(dvData.checkin.salary_override.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </KVRow>
                </InfoBox>
              )}
            </>
          )}
        </div>

        {/* Skip late deduct */}
        {hrMgmt && onSkipLateDeduct && dvData?.checkin && (
          <div style={{ padding: '0 20px 12px' }}>
            <hr style={{ border: 'none', borderTop: DIV, margin: '0 0 12px' }} />
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: dvData.checkin.skip_late_deduct ? 'rgba(249,115,22,0.07)' : 'transparent',
              padding: '8px 12px', borderRadius: 4,
              border: `1px solid ${dvData.checkin.skip_late_deduct ? 'rgba(249,115,22,0.35)' : '#e5e7eb'}`,
            }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: dvData.checkin.skip_late_deduct ? '#ea580c' : '#6b7280' }}>
                  {dvData.checkin.skip_late_deduct ? '✓ ยกเว้นหักสายแล้ว' : 'ยกเว้นหักสาย'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {dvData.checkin.skip_late_deduct ? 'คลิก toggle เพื่อกลับมาหักสายตามปกติ' : 'เปิดเพื่อไม่หักเงินมาสายวันนี้'}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: skipLateDvSaving ? 'not-allowed' : 'pointer' }}>
                <input type="checkbox" checked={!!dvData.checkin.skip_late_deduct}
                  onChange={(e) => saveSkipLateDv(e.target.checked)} disabled={skipLateDvSaving} />
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>เปิด/ปิด</span>
              </label>
            </div>
          </div>
        )}

        <div style={{ padding: '10px 20px 16px', borderTop: DIV, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={closeAll} style={{ padding: '6px 20px', borderRadius: 4, border: 'none', backgroundColor: '#6366f1', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
            ปิด
          </button>
        </div>
      </div>

      <ImageZoomDialog src={zoomSrc} alt="รูปลงเวลา" onClose={() => setZoomSrc(null)} />
    </div>
  );
});

export default DayDetailView;
